import { ComponentProps, ReactNode, Ref, cloneElement, isValidElement, useRef } from 'react';
import { mergeProps, useTooltipTrigger } from 'react-aria';
import { useTooltipTriggerState } from 'react-stately';

import composeRefs from '@seznam/compose-react-refs';

import TooltipOverlay from './TooltipOverlay';
import { pickTooltipTriggerProps } from './utils';

type Props = {
  // Why extend the type with the ref? It makes it possible to do the ref composition
  // without a bunch of weird, hacky, and brittle type-narrowing. Alternative approaches
  // are welcome...!
  children: ReactNode;
  content: ComponentProps<typeof TooltipOverlay>['children'];
  isDisabled?: Parameters<typeof useTooltipTrigger>[0]['isDisabled'];
  /** Normally, moving the mouse from the trigger to the tooltip dismisses the
   * tooltip. If you set this to true, it enables the ability to mouse into the
   * tooltip. This is useful if you want to put a link inside a tooltip.
   *
   * @default false
   */
  isInteractive?: boolean;
  isOpen?: boolean;
  /** @default 8 */
  offset?: ComponentProps<typeof TooltipOverlay>['offset'];
  onClose?: () => void;
  onOpen?: () => void;
  /** @default 'top' */
  placement?: ComponentProps<typeof TooltipOverlay>['placement'];
};

/** Wraps an element or component that should act as the trigger for displaying a tooltip. */
export default function Tooltip(_props: Props) {
  const props = { ..._props };
  props.offset ??= 8;
  props.placement ??= 'top';

  const state = useTooltipTriggerState({
    closeDelay: 0,
    delay: 100,
    isOpen: props.isOpen,
    onOpenChange: (isOpen) => {
      if (isOpen) props.onOpen?.();
      else props.onClose?.();
    },
  });
  const triggerRef = useRef<HTMLDivElement>(null);

  // Get props for the trigger and its tooltip
  const { triggerProps, tooltipProps } = useTooltipTrigger(
    {
      isDisabled: props.isDisabled || !props.content,
    },
    state,
    triggerRef
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  type ChildProps = Record<string, any> | null | undefined;

  // Check if children are valid
  if (!isValidElement<ChildProps>(props.children)) {
    throw Error('Not valid children.');
  }

  // Compose with children ref if there is one
  let ref: Ref<Element> = triggerRef;
  if (props.children.props?.ref) {
    ref = composeRefs(triggerRef, props.children.props.ref);
  }

  // Clone children, passing in tooltip trigger props
  const childrenWithTrigger = cloneElement(
    props.children,
    mergeProps(props.children.props, {
      ref,
      ...pickTooltipTriggerProps(triggerProps),
    })
  );

  return (
    <>
      {childrenWithTrigger}
      {state.isOpen && (
        <TooltipOverlay
          {...mergeProps(props, tooltipProps)}
          offset={props.isInteractive ? 0 : props.offset}
          placement={props.placement}
          state={state}
          transparentPadding={props.isInteractive ? props.offset : 0}
          triggerRef={triggerRef}
        >
          {props.content}
        </TooltipOverlay>
      )}
    </>
  );
}
